import { motion } from 'framer-motion';
import { ClipboardList, UserCog, Eye, CheckSquare, ArrowRight } from 'lucide-react';

const personas = [
  { role: 'Creator', desc: 'Creates the survey, builds questions, configures audience and schedule', icon: ClipboardList, color: 'bg-teal-600', light: 'bg-teal-50 border-teal-200', actions: ['Create survey', 'Build questionnaire', 'Configure audience', 'Submit for approval'] },
  { role: 'Reviewer', desc: 'Reviews the submitted survey, can return for edits or submit to approver', icon: Eye, color: 'bg-blue-600', light: 'bg-blue-50 border-blue-200', actions: ['Review survey', 'Return for edits', 'Submit to approver'] },
  { role: 'Publisher', desc: 'Final approver who can either approve to publish or reject the survey', icon: CheckSquare, color: 'bg-amber-600', light: 'bg-amber-50 border-amber-200', actions: ['Approve & publish', 'Reject survey'] },
];

export default function SurveyPersonasScene() {
  return (
    <div data-testid="scene-survey-personas" className="flex flex-col items-center justify-center h-full px-8">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="w-full max-w-4xl">
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-body font-medium bg-teal-600/10 text-teal-700 mb-3">
            <ClipboardList size={14} /> Survey Creation
          </span>
          <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight text-[#0A0A0A]">Survey Workflow</h2>
          <p className="mt-2 font-body text-base text-[#737373]">Three personas drive the survey lifecycle</p>
        </motion.div>

        {/* Flow arrow */}
        <div className="flex items-center justify-center gap-0 mb-8">
          {personas.map((p, i) => (
            <motion.div key={p.role} initial={{ opacity: 0, x: -15 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 + i * 0.2 }}
              className="flex items-center">
              <div className={`px-5 py-2 rounded-xl ${p.color} text-white font-heading font-bold text-sm`}>{p.role}</div>
              {i < personas.length - 1 && (
                <ArrowRight size={20} className="text-[#737373] mx-3" />
              )}
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-5">
          {personas.map((p, i) => (
            <motion.div
              key={p.role}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 + i * 0.2 }}
              className={`rounded-2xl border p-5 ${p.light}`}
            >
              <div className={`w-11 h-11 rounded-xl ${p.color} flex items-center justify-center mb-4`}>
                <p.icon size={20} className="text-white" />
              </div>
              <h3 className="font-heading font-bold text-lg text-[#0A0A0A] mb-1">{p.role}</h3>
              <p className="font-body text-sm text-[#737373] mb-4 leading-relaxed">{p.desc}</p>
              <div className="space-y-1.5">
                {p.actions.map((a, j) => (
                  <motion.div key={a} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.2 + i * 0.2 + j * 0.1 }}
                    className="flex items-center gap-2 text-[12px] font-body text-[#0A0A0A]">
                    <div className={`w-1.5 h-1.5 rounded-full ${p.color}`} />
                    {a}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}
